import { RendererProps } from '../types'
import Context from './AppContext'
import React, { useContext, ComponentType, useCallback, useMemo } from 'react'
import { ErrorBoundary, DeadComp } from './ErrorBoundary'
import _ from 'lodash'
import {
	getDisplayedId,
	getDefaultCompId,
	getTemplateFromInflatedId,
	getInflatedItemId,
	saveInflatedIdToMaps,
	isRepeatedComponent,
	REF_DELIMITER,
} from '@wix/thunderbolt-commons'
import { useProps, useStoresObserver } from './hooks'
import { getChildScope, getScopesAttributes, emptyScope, getSlotsScope } from './scopesApi'
import type { ScopeData } from './scopesApi'
import type { AppStructureWithFeatures } from '@wix/thunderbolt-symbols'
import type { ElementData, TemplateContainerProps } from '@wix/thunderbolt-becky-types'

// id is the actual DOM id and compId is the id of the comp in the structure
type StructureComponentProps = {
	id: string
	compId?: string
	scopeData: ScopeData
	displayedItemId?: string
}
const renderComp = (
	propsStore: RendererProps['props'],
	childId: string,
	scopeData: ScopeData,
	displayedItemId?: string
) => {
	const childProps = propsStore.get(childId)

	const defaultChildId = getDefaultCompId(childId)
	return (
		<StructureComponent
			displayedItemId={displayedItemId}
			compId={childId}
			scopeData={scopeData}
			id={defaultChildId}
			key={
				childProps?.key || (displayedItemId ? getDisplayedId(defaultChildId, displayedItemId!) : defaultChildId)
			}
		/>
	)
}
const StructureComponent: ComponentType<StructureComponentProps> = React.memo(
	({ id, compId = id, displayedItemId = '', scopeData = emptyScope }) => {
		const { structure: structureStore, disabledComponents, resolveRoutingBlocker } = useContext(Context)
		const displayedId = displayedItemId ? getDisplayedId(compId, displayedItemId) : compId

		const structure = structureStore.get(compId)
		const displayedStructure = structureStore.get(displayedId)
		const compStructure = displayedStructure ? { ...structure, ...displayedStructure } : structure || {}
		const { componentType, uiType, builderType } = compStructure
		const isCompDisabled = !!disabledComponents[componentType]
		const compClassType = uiType ? `${componentType}_${uiType}` : builderType || componentType

		useStoresObserver(compId, displayedId)
		resolveRoutingBlocker(displayedId)

		return (
			<StructureComponentInner
				id={id}
				key={`${id}_${compClassType}`}
				compId={compId}
				displayedItemId={displayedItemId}
				scopeData={scopeData}
				compStructure={compStructure}
				compClassType={compClassType}
				isCompDisabled={isCompDisabled}
			/>
		)
	}
)

type StructureComponentInnerProps = StructureComponentProps & {
	compStructure: AppStructureWithFeatures['components'][string]
	compClassType: string
	isCompDisabled: boolean
}

const StructureComponentInner: ComponentType<StructureComponentInnerProps> = ({
	id,
	compId = id,
	displayedItemId = '',
	scopeData = emptyScope,
	compStructure,
	compClassType,
	isCompDisabled,
}) => {
	const isDs = process.env.PACKAGE_NAME === 'thunderbolt-ds'
	const {
		props: propsStore,
		logger,
		DeletedComponent,
		BaseComponent,
		getComponentToRender,
		experiments,
		notifyError,
	} = useContext(Context)

	let displayedId = displayedItemId ? getDisplayedId(compId, displayedItemId) : compId
	const Comp: React.ComponentType<any> = getComponentToRender(compClassType, compStructure.indexInParent)
	if (!Comp) {
		notifyError?.({ type: 'feature not supported in app', error: new Error('Component Missing') })
		console.warn('Unknown component type for id', displayedId, '->', compClassType)
		logger.captureError(new Error(`Unknown component type for id ${displayedId} ${compClassType}`), {
			tags: { feature: 'StructureComponent' },
			extra: { compId: displayedId, compType: compClassType },
		})
	}

	const { containersData, ...compProps } = useProps(displayedId, compId)

	const components = compStructure!.components
	const parentScope = scopeData
	const children = useCallback(
		(childScopeData?: { scopeId: string; parentType: string; itemIndex?: number }) =>
			(components || []).map((childId) => {
				let itemId = displayedItemId
				if (childScopeData?.parentType === 'Repeater') {
					itemId = getInflatedItemId(childScopeData!.scopeId, displayedItemId)
				}

				const childScope = isDs ? getChildScope(compId, parentScope, childScopeData) : emptyScope
				return renderComp(propsStore, childId, childScope, itemId)
			}),
		[components, displayedItemId, compId, parentScope, propsStore, isDs]
	)

	const slots = compStructure!.slots

	const scopeAttr = isDs ? getScopesAttributes(scopeData) : {}
	const shouldRenderComp = !isCompDisabled && Comp

	// TODO: Remove the fallback once all components are implemented
	// in case comp is not inside repeater, remove hover box suffix if exist
	displayedId = displayedItemId ? displayedId : getDefaultCompId(id)

	const isFakeInflationForDS =
		isDs &&
		experiments['specs.thunderbolt.DontInflateMasterPage'] &&
		!experiments['specs.thunderbolt.doNotInflateSharedParts']

	let inflatedId = displayedId

	if (isFakeInflationForDS) {
		// for repeaters			: displayedId = templateId = id + REPEATER_DELIMITER
		// for blocks   			: displayedId = id         = REF_DELIMITERS + templateId
		// for repeater item inside block:
		//     displayedId = REF_DELIMITERS + [REF_COMP_ID] + REPEATER_DELIMITER
		//     templateId  = 				  [REF_COMP_ID] + REPEATER_DELIMITER
		//     id		   = REF_DELIMITERS + [REF_COMP_ID]
		const templateId = getTemplateFromInflatedId(displayedId)

		const scope = scopeData.scope.filter((ancestor) => {
			return !!ancestor && !isRepeatedComponent(ancestor)
		}) as Array<string>

		function inflateIdWithScope(idToInflate: string) {
			return `${[...scope, idToInflate].join(REF_DELIMITER)}`
		}

		inflatedId = inflateIdWithScope(templateId)

		if (inflatedId !== displayedId) {
			saveInflatedIdToMaps(displayedId, inflatedId)

			// for repeater items we also need to save id without REPEATER_DELIMITER part
			if (id !== displayedId) {
				saveInflatedIdToMaps(id, inflateIdWithScope(getTemplateFromInflatedId(id)))
			}
		}
	}

	const slotNeedsFakeInflation = isFakeInflationForDS && inflatedId !== displayedId

	const getSlotByItemId = useMemo(
		() => (slotName: string, itemId: string) => {
			const slotId = slots?.[`${slotName}`]
			if (!slotId) {
				return null
			}
			const slotIdForScopeGetter = slotNeedsFakeInflation
				? parentScope.scope.at(0)! + REF_DELIMITER + slotId
				: slotId

			const slotsScope = getSlotsScope(parentScope, slotIdForScopeGetter)
			return renderComp(propsStore, slotId, slotsScope, itemId)
		},
		[slots, propsStore, parentScope, slotNeedsFakeInflation]
	)

	const slotsProps = useMemo(
		() =>
			_.mapValues(slots, (slotId) => {
				const slotIdForScopeGetter = slotNeedsFakeInflation
					? parentScope.scope.at(0)! + REF_DELIMITER + slotId
					: slotId

				const slotsScope = getSlotsScope(parentScope, slotIdForScopeGetter)
				return renderComp(propsStore, slotId, slotsScope, displayedItemId)
			}),
		[slots, displayedItemId, propsStore, parentScope, slotNeedsFakeInflation]
	)

	const containerProps: { [key: string]: React.JSX.Element } | TemplateContainerProps | {} = useMemo(() => {
		const shouldAddBuilderContainerProps = experiments['specs.thunderbolt.enableBuilderContainerProps']
		if (!shouldAddBuilderContainerProps) {
			return {}
		}

		const containers: NonNullable<ElementData['containersData']> = containersData || {}

		const templateContainers = _.pickBy(containers, (containerData) => containerData.containerType === 'template')

		if (Object.keys(templateContainers).length > 0) {
			return Object.fromEntries(
				Object.entries(templateContainers).map(([propertyName]) => [
					propertyName,
					(itemId: string, itemIndex: number) =>
						children({ parentType: 'Repeater', scopeId: itemId, itemIndex }),
				])
			)
		}

		// TODO: check container type: simple, placeholder or slot.
		// Slot: should pass data items as a function to the component

		return slotsProps
	}, [experiments, slotsProps, containersData, children])

	let component

	if (compStructure.deleted) {
		component = <DeletedComponent BaseComponent={BaseComponent} id={displayedId} compId={compId} />
	} else if (!shouldRenderComp) {
		component = <DeadComp id={displayedId} />
	} else {
		component = (
			<Comp
				{...containerProps}
				compProps={compProps}
				getSlotByItemId={getSlotByItemId}
				{...scopeAttr}
				slots={slotsProps}
				id={displayedId}
				compId={compId}
				{...(inflatedId !== displayedId ? { 'data-inflated-id': inflatedId } : {})}
				compClassType={compClassType}
			>
				{children}
			</Comp>
		)
	}

	return (
		<ErrorBoundary
			id={displayedId}
			deleted={compStructure.deleted}
			logger={logger}
			Component={Comp}
			compClassType={compClassType}
		>
			{component}
		</ErrorBoundary>
	)
}

export default StructureComponent
